import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Chip,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import { Booking, useCreateBooking, useUpdateBooking } from '../../hooks/api/booking';
import { User, useGetUsers } from '../../hooks/api/user';
import GraduationProgressBar from '../student/GraduationProgressBar';
import { getUserGraduationProgress } from '../student/studentGraduation';

interface DesktopParticipantsListProps {
  bookings: Booking[];
  lessonId: number;
}

const getParticipantName = (booking: Booking) => {
  return booking.user.nickName ?? `${booking.user.firstName} ${booking.user.lastName}`;
};

const getStudentName = (user: User) => {
  return user.nickName ?? `${user.firstName} ${user.lastName}`;
};

const getStatusLabel = (status: Booking['status']) => {
  switch (status) {
    case 'approved':
      return 'Aprobada';
    case 'declined':
      return 'Declinada';
    case 'pending':
      return 'Pendiente';
  }
};

const getStatusStyles = (status: Booking['status']) => {
  switch (status) {
    case 'approved':
      return { bgcolor: '#e9f6ec', color: '#1d6b32' };
    case 'declined':
      return { bgcolor: '#f7eeee', color: '#8a2d2d' };
    case 'pending':
      return { bgcolor: '#fff4de', color: '#765019' };
  }
};

export default function DesktopParticipantsList({
  bookings,
  lessonId,
}: DesktopParticipantsListProps) {
  const [selectedStudent, setSelectedStudent] = useState<User | null>(null);
  const createBookingMutation = useCreateBooking();
  const updateBookingMutation = useUpdateBooking();
  const usersQuery = useGetUsers({ hasPlan: true });

  const bookedUserIds = useMemo(() => {
    return new Set(bookings.map((booking) => booking.userId));
  }, [bookings]);

  const availableStudents = useMemo(() => {
    const users = usersQuery.data ?? [];
    return users.filter((user) => !user.instructor && !bookedUserIds.has(user.id));
  }, [bookedUserIds, usersQuery.data]);

  const sortedBookings = useMemo(() => {
    const statusOrder: Record<Booking['status'], number> = {
      pending: 0,
      approved: 1,
      declined: 2,
    };
    return [...bookings].sort((firstBooking, secondBooking) => {
      return statusOrder[firstBooking.status] - statusOrder[secondBooking.status];
    });
  }, [bookings]);

  const addAcceptedBooking = () => {
    if (!selectedStudent) {
      return;
    }

    createBookingMutation.mutate(
      {
        lessonId,
        userId: selectedStudent.id,
        status: 'approved',
      },
      {
        onSuccess: () => setSelectedStudent(null),
      },
    );
  };

  const updateBooking = (bookingId: number, status: Booking['status']) => {
    updateBookingMutation.mutate({
      bookingId,
      changeset: { status },
    });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 4,
        border: '1px solid rgba(18,18,18,0.08)',
        backgroundColor: 'white',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Participantes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gestiona reservas, asistencias y altas manuales desde aquí
          </Typography>
        </Box>
        <Chip
          label={`${bookings.length} ${bookings.length === 1 ? 'persona' : 'personas'}`}
          size="small"
          sx={{ bgcolor: '#f3f3f3', color: '#111111', fontWeight: 600 }}
        />
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 1.5,
          borderRadius: 3,
          border: '1px solid rgba(18,18,18,0.08)',
          backgroundColor: '#f7f7f7',
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch">
          <Autocomplete
            value={selectedStudent}
            onChange={(_, user) => setSelectedStudent(user)}
            options={availableStudents}
            getOptionLabel={getStudentName}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="No hay alumnos disponibles"
            size="small"
            sx={{ flex: 1, bgcolor: 'white' }}
            renderInput={(params) => <TextField {...params} placeholder="Buscar alumno" />}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!selectedStudent || createBookingMutation.isLoading}
            onClick={addAcceptedBooking}
            sx={{
              borderRadius: 2,
              boxShadow: 'none',
              textTransform: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            Añadir aprobado
          </Button>
        </Stack>
      </Paper>

      {sortedBookings.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Aún no hay participantes registrados para esta clase.
        </Typography>
      ) : (
        <Stack divider={<Divider flexItem />} spacing={0}>
          {sortedBookings.map((booking) => {
            const progress = getUserGraduationProgress(booking.user);

            return (
              <Stack
                key={booking.id}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ py: 1.5 }}
              >
                <ButtonBase
                  component="a"
                  href={`/students/${booking.userId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    minWidth: 0,
                    flex: 1,
                    borderRadius: 2,
                    display: 'block',
                    py: 0.75,
                    px: 0.75,
                    textAlign: 'left',
                    '&:hover': { bgcolor: 'rgba(17, 17, 17, 0.04)' },
                    '&:focus-visible': {
                      outline: '2px solid rgba(17, 17, 17, 0.35)',
                      outlineOffset: 2,
                    },
                  }}
                >
                  <Typography sx={{ fontWeight: 700, color: '#111111' }}>
                    {getParticipantName(booking)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {`${booking.user.firstName} ${booking.user.lastName}`}
                  </Typography>
                  <Box sx={{ mt: 1, maxWidth: 420 }}>
                    <GraduationProgressBar progress={progress} compact />
                  </Box>
                </ButtonBase>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent={{ xs: 'space-between', sm: 'flex-end' }}
                  sx={{ flexShrink: 0 }}
                >
                  <Chip
                    label={getStatusLabel(booking.status)}
                    size="small"
                    sx={{ ...getStatusStyles(booking.status), fontWeight: 700 }}
                  />
                  <Stack direction="row" spacing={0.75}>
                    <Button
                      aria-label={`Aprobar reserva de ${getParticipantName(booking)}`}
                      variant={booking.status === 'approved' ? 'contained' : 'outlined'}
                      size="small"
                      startIcon={<CheckIcon sx={{ fontSize: '16px !important' }} />}
                      disabled={booking.status === 'approved' || updateBookingMutation.isLoading}
                      onClick={() => updateBooking(booking.id, 'approved')}
                      sx={{ minWidth: 0, textTransform: 'none', boxShadow: 'none' }}
                    >
                      Aprobar
                    </Button>
                    <Button
                      aria-label={`Rechazar reserva de ${getParticipantName(booking)}`}
                      color="error"
                      variant={booking.status === 'declined' ? 'contained' : 'outlined'}
                      size="small"
                      startIcon={<ClearIcon sx={{ fontSize: '16px !important' }} />}
                      disabled={booking.status === 'declined' || updateBookingMutation.isLoading}
                      onClick={() => updateBooking(booking.id, 'declined')}
                      sx={{ minWidth: 0, textTransform: 'none', boxShadow: 'none' }}
                    >
                      Rechazar
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
}
